import { useState } from 'react';
import { useSignature } from '../contexts/SignatureContext';
import { Flavor, ModePreference, Density } from '../theme/signatureTokens';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { FormArchetype } from '../components/archetypes/FormArchetype';
import { AppearanceLivePreview } from '../components/appearance/AppearanceLivePreview';
import { 
  IconPalette, 
  IconSun, 
  IconMoon, 
  IconDeviceDesktop,
  IconCheck, 
  IconRefresh, 
  IconDeviceFloppy, 
  IconLock,
  IconGlobe,
  IconBuilding,
  IconUser,
  IconUsers,
  IconAlertTriangle,
  IconAdjustmentsHorizontal
} from '@tabler/icons-react';

export default function AppearancePage() {
  const { t, i18n } = useTranslation();
  const { flavor, modePref, density, locale, colors, savePreferences } = useSignature();
  const { getUserContext, getEmpresaNome } = useAuth();

  const userContext = getUserContext();
  const empresaNome = getEmpresaNome() || 'Empresa Ativa';

  // 1. Estado de Rascunho Isolado para Live Preview (sem mutar app global até clicar em Salvar)
  const [draftFlavor, setDraftFlavor] = useState<Flavor>(flavor);
  const [draftMode, setDraftMode] = useState<ModePreference>(modePref);
  const [draftDensity, setDraftDensity] = useState<Density>(density);
  const [draftLanguage, setDraftLanguage] = useState<string>(i18n.language || 'pt-BR');
  const [draftLocale, setDraftLocale] = useState<string>(locale || 'pt-BR');
  const [draftScope, setDraftScope] = useState<'user' | 'company' | 'group'>('user');

  const [savedSuccess, setSavedSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // 2. Verificação de Governança/Permissão de Alteração de Sabor
  const isMasterOrAdmin = ['SUPER_ADMIN', 'ADMINISTRADOR', 'ADMIN', 'MASTER'].includes(String(userContext?.nivel_acesso_efetivo || userContext?.role || '').toUpperCase());

  // 3. Restaurar Padrão (SIGNATURE + LIGHT + COMFORTABLE + pt-BR)
  const handleRestoreDefaults = () => {
    setDraftFlavor('SIGNATURE');
    setDraftMode('light');
    setDraftDensity('comfortable');
    setDraftLanguage('pt-BR');
    setDraftLocale('pt-BR');
    setDraftScope('user');
  };

  // 4. Salvar Preferências (runtime global + backend por usuário + empresa)
  const handleSavePreferences = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await savePreferences({
        flavor: draftFlavor,
        modePref: draftMode,
        density: draftDensity,
        locale: draftLocale,
      });
      if (draftLanguage !== i18n.language) {
        i18n.changeLanguage(draftLanguage);
      }
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch {
      setSaveError('Não foi possível salvar no servidor. A preferência ficou aplicada apenas neste navegador.');
    } finally {
      setSaving(false);
    }
  };

  const flavorsList = [
    { 
      id: 'SIGNATURE' as Flavor, 
      name: 'SIGNATURE', 
      tag: 'Padrão Oficial ERP',
      desc: 'Equilíbrio contemporâneo corporativo em tom azul Galatea confortavelmente ajustado para uso diário prolongado.'
    },
    { 
      id: 'EXECUTIVE' as Flavor, 
      name: 'EXECUTIVE', 
      tag: 'Gestão & KPIs',
      desc: 'Profundidade visual elevada, contraste expandido e foco em análise estratégica e painéis de indicadores.'
    },
    { 
      id: 'PROCESS' as Flavor, 
      name: 'PROCESS', 
      tag: 'Processos & Implantação',
      desc: 'Identidade luminosa em tom teal/cyan, ideal para etapas guiadas, formulários extensos e fluxos operacionais.'
    },
    { 
      id: 'MINIMAL' as Flavor, 
      name: 'MINIMAL', 
      tag: 'Operação Intensiva',
      desc: 'Layout cirúrgico em tom graphite slate com linhas de precisão, maximizando a densidade sem distrações.'
    },
  ];

  const localesList = [
    { code: 'pt-BR', name: 'Brasil (pt-BR)', currency: 'R$', dateFormat: 'DD/MM/AAAA' },
    { code: 'es-UY', name: 'Uruguai (es-UY)', currency: '$U', dateFormat: 'DD/MM/AAAA' },
    { code: 'es-PY', name: 'Paraguai (es-PY)', currency: '₲', dateFormat: 'DD/MM/AAAA' },
    { code: 'es-AR', name: 'Argentina (es-AR)', currency: '$', dateFormat: 'DD/MM/AAAA' },
    { code: 'es-CL', name: 'Chile (es-CL)', currency: '$', dateFormat: 'DD/MM/AAAA' },
    { code: 'en-US', name: 'Estados Unidos (en-US)', currency: '$', dateFormat: 'MM/DD/YYYY' },
  ];

  return (
    <FormArchetype
      title={t('config.appearanceTitle', 'Aparência e Identidade Visual')}
      subtitle={`Governança do Galatea Signature · Empresa: ${empresaNome}`}
      actionBar={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            {savedSuccess && (
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 animate-pulse">
                <IconCheck size={16} /> Preferências salvas com sucesso!
              </span>
            )}
            {saveError && (
              <span className="text-xs font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <IconAlertTriangle size={16} /> {saveError}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleRestoreDefaults}
              className="px-4 py-2 text-xs font-bold rounded-lg border transition-all flex items-center gap-2"
              style={{
                backgroundColor: colors.bgSurface,
                borderColor: colors.border,
                color: colors.textSecondary,
              }}
            >
              <IconRefresh size={15} />
              Restaurar Padrões
            </button>

            <button
              type="button"
              onClick={handleSavePreferences}
              disabled={saving}
              className="px-5 py-2 text-xs font-bold text-white rounded-lg shadow-md transition-all flex items-center gap-2 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: colors.primary }}
            >
              <IconDeviceFloppy size={16} />
              {saving ? 'Salvando...' : 'Salvar Preferências'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Seção 1: Sabores Visuais Oficiais (Galatea Signature Flavors) */}
        <section className="p-5 rounded-xl border shadow-xs" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <IconPalette size={20} className="text-sky-600 dark:text-sky-400" />
              <h2 className="text-sm font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                1. Sabores Visuais Oficiais (Galatea Signature Flavors)
              </h2>
            </div>

            {!isMasterOrAdmin && (
              <span className="text-[11px] px-2.5 py-1 rounded font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 flex items-center gap-1">
                <IconLock size={13} /> Sabor restrito pela administração
              </span>
            )}
          </div>

          <p className="text-xs mb-4" style={{ color: colors.textSecondary }}>
            Selecione a variante do Design System soberano Galatea. Cada sabor possui paletas exclusivas para os modos Claro e Escuro.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {flavorsList.map((f) => {
              const isSelected = draftFlavor === f.id;
              return (
                <div
                  key={f.id}
                  onClick={() => setDraftFlavor(f.id)}
                  className={`p-4 rounded-xl border cursor-pointer transition-all flex flex-col justify-between relative ${
                    isSelected ? 'ring-2 ring-sky-500 border-transparent shadow-sm' : 'hover:border-sky-400'
                  }`}
                  style={{
                    backgroundColor: isSelected ? colors.bgSurfaceHover : colors.bgSurface,
                    borderColor: isSelected ? colors.primary : colors.border,
                  }}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3 text-sky-600 dark:text-sky-400">
                      <IconCheck size={18} />
                    </div>
                  )}

                  <div>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-sky-500/10 text-sky-600 dark:text-sky-400 mb-2 inline-block">
                      {f.tag}
                    </span>
                    <h3 className="text-sm font-bold mt-1" style={{ color: colors.textPrimary }}>
                      {f.name}
                    </h3>
                    <p className="text-xs mt-1.5 leading-relaxed" style={{ color: colors.textSecondary }}>
                      {f.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Seção 2: Modo de Luminosidade & Densidade Operacional */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Modo de Cor / Luminosidade */}
          <section className="p-5 rounded-xl border shadow-xs" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}>
            <div className="flex items-center gap-2 mb-2">
              <IconSun size={20} className="text-amber-500" />
              <h2 className="text-sm font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                2. Modo de Luminosidade
              </h2>
            </div>

            <p className="text-xs mb-4" style={{ color: colors.textSecondary }}>
              Alterna entre o tom Claro Azulado (Ice/Slate, sem brancos puros), o Escuro (Marinho / Deep Navy) ou o modo Auto, que segue a preferência do sistema operacional.
            </p>

            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setDraftMode('light')}
                className={`p-3 rounded-lg border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                  draftMode === 'light' ? 'ring-2 ring-sky-500 border-sky-500' : ''
                }`}
                style={{
                  backgroundColor: draftMode === 'light' ? colors.bgSurfaceHover : colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              >
                <IconSun size={16} className="text-amber-500" />
                Claro Azulado (Ice)
              </button>

              <button
                type="button"
                onClick={() => setDraftMode('dark')}
                className={`p-3 rounded-lg border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                  draftMode === 'dark' ? 'ring-2 ring-sky-500 border-sky-500' : ''
                }`}
                style={{
                  backgroundColor: draftMode === 'dark' ? colors.bgSurfaceHover : colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              >
                <IconMoon size={16} className="text-sky-400" />
                Escuro (Deep Navy)
              </button>

              <button
                type="button"
                onClick={() => setDraftMode('auto')}
                className={`p-3 rounded-lg border text-xs font-bold flex items-center justify-center gap-2 transition-all ${
                  draftMode === 'auto' ? 'ring-2 ring-sky-500 border-sky-500' : ''
                }`}
                style={{
                  backgroundColor: draftMode === 'auto' ? colors.bgSurfaceHover : colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              >
                <IconDeviceDesktop size={16} className="text-emerald-500" />
                Auto (Sistema)
              </button>
            </div>
          </section>

          {/* Densidade Operacional (Sem Pixels Expostos) */}
          <section className="p-5 rounded-xl border shadow-xs" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}>
            <div className="flex items-center gap-2 mb-2">
              <IconAdjustmentsHorizontal size={20} className="text-sky-600 dark:text-sky-400" />
              <h2 className="text-sm font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                3. Densidade Operacional
              </h2>
            </div>

            <p className="text-xs mb-4" style={{ color: colors.textSecondary }}>
              Ajusta o aproveitamento de espaço e altura de componentes para seu perfil de uso diário.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setDraftDensity('comfortable')}
                className={`p-3 rounded-lg border text-xs font-bold flex flex-col items-center justify-center text-center gap-1 transition-all ${
                  draftDensity === 'comfortable' ? 'ring-2 ring-sky-500 border-sky-500' : ''
                }`}
                style={{
                  backgroundColor: draftDensity === 'comfortable' ? colors.bgSurfaceHover : colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              >
                <span>Comfortable</span>
                <span className="text-[10px] font-normal" style={{ color: colors.textSecondary }}>
                  Focado em conforto visual para formulários e uso contínuo.
                </span>
              </button>

              <button
                type="button"
                onClick={() => setDraftDensity('compact')}
                className={`p-3 rounded-lg border text-xs font-bold flex flex-col items-center justify-center text-center gap-1 transition-all ${
                  draftDensity === 'compact' ? 'ring-2 ring-sky-500 border-sky-500' : ''
                }`}
                style={{
                  backgroundColor: draftDensity === 'compact' ? colors.bgSurfaceHover : colors.bgSurface,
                  borderColor: colors.border,
                  color: colors.textPrimary,
                }}
              >
                <span>Compact</span>
                <span className="text-[10px] font-normal" style={{ color: colors.textSecondary }}>
                  Focado em aproveitamento máximo de espaço para tabelas.
                </span>
              </button>
            </div>
          </section>
        </div>

        {/* Seção 3: Idioma da Interface & Regionalização (Separados) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Idioma da Interface */}
          <section className="p-5 rounded-xl border shadow-xs" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}>
            <div className="flex items-center gap-2 mb-2">
              <IconGlobe size={20} className="text-sky-600 dark:text-sky-400" />
              <h2 className="text-sm font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                4. Idioma da Interface (i18n)
              </h2>
            </div>

            <p className="text-xs mb-4" style={{ color: colors.textSecondary }}>
              Define o idioma de exibição de menus, botões, mensagens e assistentes de IA.
            </p>

            <div className="grid grid-cols-3 gap-2">
              {[
                { code: 'pt-BR', label: 'Português' },
                { code: 'es', label: 'Español' },
                { code: 'en', label: 'English' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => setDraftLanguage(lang.code)}
                  className={`p-2.5 rounded-lg border text-xs font-bold transition-all ${
                    draftLanguage === lang.code ? 'ring-2 ring-sky-500 border-sky-500' : ''
                  }`}
                  style={{
                    backgroundColor: draftLanguage === lang.code ? colors.bgSurfaceHover : colors.bgSurface,
                    borderColor: colors.border,
                    color: colors.textPrimary,
                  }}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </section>

          {/* Regionalização / Formatação */}
          <section className="p-5 rounded-xl border shadow-xs" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}>
            <div className="flex items-center gap-2 mb-2">
              <IconBuilding size={20} className="text-sky-600 dark:text-sky-400" />
              <h2 className="text-sm font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                5. Regionalização & Formatação (Locale)
              </h2>
            </div>

            <p className="text-xs mb-3" style={{ color: colors.textSecondary }}>
              Controla o símbolo de moeda local, separadores numéricos e formato de datas.
            </p>

            <select
              value={draftLocale}
              onChange={(e) => setDraftLocale(e.target.value)}
              className="w-full p-2.5 rounded-lg text-xs font-bold border bg-transparent focus:outline-none focus:ring-2 focus:ring-sky-500"
              style={{
                backgroundColor: colors.bgSurface,
                borderColor: colors.border,
                color: colors.textPrimary,
              }}
            >
              {localesList.map((loc) => (
                <option key={loc.code} value={loc.code} style={{ backgroundColor: colors.bgSurface, color: colors.textPrimary }}>
                  {loc.name} · Moeda: {loc.currency} ({loc.dateFormat})
                </option>
              ))}
            </select>
          </section>
        </div>

        {/* Seção 4: Escopo de Aplicação (Governança) */}
        <section className="p-5 rounded-xl border shadow-xs" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}>
          <div className="flex items-center gap-2 mb-2">
            <IconUsers size={20} className="text-sky-600 dark:text-sky-400" />
            <h2 className="text-sm font-bold tracking-tight" style={{ color: colors.textPrimary }}>
              6. Escopo de Aplicação da Preferência
            </h2>
          </div>

          <p className="text-xs mb-4" style={{ color: colors.textSecondary }}>
            Nesta versão a preferência é salva por usuário + empresa. A aplicação para toda a empresa ou para o grupo empresarial exige contrato de backend dedicado (pendente de governança).
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setDraftScope('user')}
              className={`p-3 rounded-lg border text-xs font-bold flex items-center gap-2 transition-all ${
                draftScope === 'user' ? 'ring-2 ring-sky-500 border-sky-500' : ''
              }`}
              style={{
                backgroundColor: draftScope === 'user' ? colors.bgSurfaceHover : colors.bgSurface,
                borderColor: colors.border,
                color: colors.textPrimary,
              }}
            >
              <IconUser size={16} className="text-sky-500" />
              <span>Somente Meu Usuário</span>
            </button>

            <button
              type="button"
              disabled
              title="Pendente: requer backend dedicado para aplicar a preferência a toda a empresa"
              className={`p-3 rounded-lg border text-xs font-bold flex items-center gap-2 transition-all ${
                draftScope === 'company' ? 'ring-2 ring-sky-500 border-sky-500' : ''
              } opacity-50 cursor-not-allowed`}
              style={{
                backgroundColor: draftScope === 'company' ? colors.bgSurfaceHover : colors.bgSurface,
                borderColor: colors.border,
                color: colors.textPrimary,
              }}
            >
              <IconBuilding size={16} className="text-amber-500" />
              <span>Toda a Empresa (Tenant)</span>
            </button>

            <button
              type="button"
              disabled
              title="Pendente: requer backend dedicado para aplicar a preferência a todo o grupo empresarial"
              className={`p-3 rounded-lg border text-xs font-bold flex items-center gap-2 transition-all ${
                draftScope === 'group' ? 'ring-2 ring-sky-500 border-sky-500' : ''
              } opacity-50 cursor-not-allowed`}
              style={{
                backgroundColor: draftScope === 'group' ? colors.bgSurfaceHover : colors.bgSurface,
                borderColor: colors.border,
                color: colors.textPrimary,
              }}
            >
              <IconUsers size={16} className="text-purple-500" />
              <span>Todo o Grupo Empresarial</span>
            </button>
          </div>
        </section>

        {/* Seção 5: Live System Preview (Galatea Canvas ao Vivo) */}
        <section className="p-5 rounded-xl border shadow-xs space-y-3" style={{ backgroundColor: colors.bgSurface, borderColor: colors.border }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <IconPalette size={20} className="text-sky-600 dark:text-sky-400" />
              <h2 className="text-sm font-bold tracking-tight" style={{ color: colors.textPrimary }}>
                7. Live System Preview (Demonstração ao Vivo)
              </h2>
            </div>

            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-sky-500/10 text-sky-600 dark:text-sky-400 font-medium">
              Rascunho Isolado · Não altera a aplicação até Salvar
            </span>
          </div>

          <p className="text-xs" style={{ color: colors.textSecondary }}>
            Visualização ao vivo com componentes reais do Galatea Signature (Marca Soberana + Logo S3 do Tenant + Ícones Semânticos Tabler + Card CONTROL + Grid).
          </p>

          <AppearanceLivePreview
            draftFlavor={draftFlavor}
            draftMode={draftMode}
            draftDensity={draftDensity}
            draftLanguage={draftLanguage}
            draftLocale={draftLocale}
          />
        </section>
      </div>
    </FormArchetype>
  );
}
